// src/main/content/contests/ContestTargetRace.js
//
// Animated "target race" for closest-to-100% contests. Each store gets a
// horizontal track with a centered target line (100%). A colored marker
// slides along the track as the daily cumulative ratio updates frame by
// frame, and rows reorder vertically as the standings change.
//
// Why a separate viz from ContestRaceReplay: cumulative metrics
// (sales, etc.) tell a "grow rightward" story where a longer bar is
// strictly better. Ratio metrics (actual / forecasted) have a target —
// 100% — that you can overshoot OR undershoot, and "closer to 100% wins"
// can't be expressed as a left-anchored bar. A center-anchored marker on
// a target line makes the deviation visible in both directions.
//
// Inputs match ContestRaceReplay's shape (post-ContestDetails
// normalization) except each row carries a ``ratio`` array of floats
// (or nulls on days where the cumulative denominator was zero) instead
// of ``daily``/``cumulative``.

import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import { TIMELINE_COLORS } from "./ContestCumulativeChart";

const ROW_HEIGHT = 48;
const TOP_N = 8;
const BASE_FRAME_MS = 600;
const SPEED_OPTIONS = [1, 2, 4];

// The track can be centered on any target value. Ratio metrics center on 1.0
// (100%); a signed-delta metric (Labor % vs Target) centers on 0 ("on
// target"). Both flow through the same marker-on-a-track machinery.

function formatValue(value, valueFormat) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  const v = Number(value);
  if (valueFormat === "points_delta") {
    // Signed percentage-point delta, e.g. "−2.8 pts" (under) / "+1.2 pts"
    // (over). toFixed carries the minus sign; we prepend "+" for positives.
    return `${v > 0 ? "+" : ""}${v.toFixed(1)} pts`;
  }
  return `${(v * 100).toFixed(1)}%`;
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0][0] || "?").toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

export default function ContestTargetRace({
  timeline,
  showStoreTag = false,
  direction = "closest_to_100",
  winnersCount = 1,
  // "top_n" (default) trophies the top winnersCount by competition rank;
  // "hit_target" trophies every row on the good side of its target — the
  // same rule the backend applies to the leaderboard's is_winner.
  winnerMode = "top_n",
  // Value the track is centered on: 1.0 for ratio metrics, 0 for signed deltas.
  centerValue = 1.0,
  // Which per-row array carries the plotted value ("ratio" or "delta").
  seriesKey = "ratio",
  // How the right-hand value label is rendered ("percentage" | "points_delta").
  valueFormat = "percentage",
  // Floor for the half-window so a race where everyone hugs the target doesn't
  // collapse markers into one column. In the metric's own units (ratio: 0.05 =
  // ±5%; points: ~1 point).
  minHalfWindow = 0.05,
  // Header caption describing the win condition.
  subtitle = "Closest to 100% wins",
  // "marker" (default): a dot slides on a track — right for two-sided metrics
  // (closest_to_100) where neither side is "good". "bar": a diverging bar grows
  // from the center target line, green on the good side / red on the bad side —
  // right for one-directional metrics (e.g. Labor % vs Target, lower is better)
  // where under-target is unambiguously good.
  variant = "marker",
}) {
  const employees = (timeline?.employees || []).slice(0, TOP_N);
  const dates = timeline?.dates || [];
  const dateCount = dates.length;
  const isEligible = dateCount >= 1 && employees.length >= 1;

  // Start at the final frame so the page initially reflects the current
  // standings; pressing play rewinds to the start.
  const [idx, setIdx] = useState(Math.max(0, dateCount - 1));
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const intervalRef = useRef(null);

  useEffect(() => {
    setIdx(Math.max(0, dateCount - 1));
    setIsPlaying(false);
  }, [dateCount]);

  useEffect(() => {
    if (!isPlaying || !isEligible) return undefined;
    const intervalMs = Math.max(80, BASE_FRAME_MS / speed);
    intervalRef.current = setInterval(() => {
      setIdx((prev) => {
        if (prev >= dateCount - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, intervalMs);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, speed, dateCount, isEligible]);

  // Symmetric half-window around the 100% target. We look at the worst
  // deviation across every (store, date) pair so the scale is stable as
  // we scrub — if we recomputed per frame, the markers would jitter
  // around when a single outlier appeared / disappeared.
  const halfWindow = useMemo(() => {
    let m = minHalfWindow;
    for (const e of employees) {
      for (const v of e[seriesKey] || []) {
        if (v === null || v === undefined) continue;
        const d = Math.abs(v - centerValue);
        if (d > m) m = d;
      }
    }
    // 15% padding so a runaway leader doesn't pin to the absolute edge
    // and lose its border + value-label legibility.
    return m * 1.15;
  }, [employees, seriesKey, centerValue, minHalfWindow]);

  // For each frame: a visual ``position`` (unique 0-indexed row used for
  // ``translateY`` so markers don't stack) and a 1-indexed ``competitionRank``
  // (ties share the same number — used for winner/leader treatment).
  //
  // We can't collapse these into one map: two stores equally 10pp from
  // target should both be "rank 1" for winner highlighting, but they
  // still need different rows to render. The tie-tolerance mirrors
  // backend/contest_info/leaderboard.py:_assign_ranks so the target
  // race agrees with the leaderboard frame-for-frame.
  const { positionById, competitionRankById } = useMemo(() => {
    const safeIdx = Math.min(idx, dateCount - 1);
    const keyFor = (v) => {
      if (direction === "closest_to_100") return Math.abs(v - centerValue);
      if (direction === "lower_is_better") return v;
      return -v;
    };
    const sortKeyOf = (e) => {
      const v = e[seriesKey]?.[safeIdx];
      if (v === null || v === undefined) return null;
      return keyFor(v);
    };
    // Mirror Python's ``math.isclose(rel_tol=1e-9, abs_tol=1e-12)`` so
    // jitter on symmetric ``closest_to_100`` keys (abs(1.1-1.0) vs
    // abs(1.0-0.9) differ at ~1e-17) collapses to a real tie.
    const REL_TOL = 1e-9;
    const ABS_TOL = 1e-12;
    const tied = (a, b) =>
      Math.abs(a - b) <= Math.max(REL_TOL * Math.max(Math.abs(a), Math.abs(b)), ABS_TOL);

    const ranked = [...employees].sort((a, b) => {
      const ak = sortKeyOf(a);
      const bk = sortKeyOf(b);
      // Null-data stores sink to the bottom regardless of direction —
      // they can't be ranked until they have at least one defined point.
      if (ak === null && bk === null) return 0;
      if (ak === null) return 1;
      if (bk === null) return -1;
      // Within tolerance: declare equal so V8's stable sort preserves
      // input order — keeps tied rows from swapping frame to frame.
      if (tied(ak, bk)) return 0;
      return ak - bk;
    });

    const positionById = {};
    const competitionRankById = {};
    // ``groupKey`` is the sort_key of the *first* member of the current
    // tie group — NOT the immediately preceding key. We only update it
    // when a new rank is assigned, so every subsequent member of the
    // group is checked against the group's anchor. This prevents
    // transitive chain-ties under sub-tolerance drift (A within ε of B,
    // B within ε of C, but A NOT within ε of C — pairwise prev-key
    // comparison would tie all three; group-leader comparison
    // correctly splits C off). Mirrors the backend's
    // ``leaderboard._assign_ranks`` so the two views agree on tie
    // boundaries.
    let groupKey = null;
    let groupRank = 0;
    ranked.forEach((e, i) => {
      positionById[e.id] = i;
      const k = sortKeyOf(e);
      // Null entries get sequential ranks at the bottom — they shouldn't
      // tie with each other or with anything else for winner purposes.
      // Reset groupKey so the next non-null entry (if any) starts a
      // fresh group.
      if (k === null) {
        competitionRankById[e.id] = i + 1;
        groupKey = null;
      } else if (groupKey !== null && tied(k, groupKey)) {
        // Still inside the current tie group — inherit its rank.
        // groupKey deliberately NOT updated so the next item also
        // compares against the original anchor.
        competitionRankById[e.id] = groupRank;
      } else {
        // First member of a new group: this becomes the anchor.
        competitionRankById[e.id] = i + 1;
        groupRank = i + 1;
        groupKey = k;
      }
    });
    return { positionById, competitionRankById };
  }, [employees, idx, dateCount, direction, centerValue, seriesKey]);

  if (!isEligible) return null;

  const safeIdx = Math.min(idx, dateCount - 1);
  const currentDate = dates[safeIdx];
  const atEnd = safeIdx >= dateCount - 1;

  const handlePlayPause = () => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    if (atEnd) setIdx(0);
    setIsPlaying(true);
  };

  // Convert a ratio value into a left-% on the track. Target sits at
  // 50%; the half-window controls how far the edges represent. Values
  // outside the window get clamped so the marker stays inside the rail.
  const valueToLeftPct = (v) => {
    if (v === null || v === undefined) return null;
    const delta = (v - centerValue) / halfWindow;
    const clamped = Math.max(-1, Math.min(1, delta));
    return 50 + clamped * 50;
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden h-full">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TimelapseIcon className="text-violet-600" style={{ fontSize: 18 }} />
          <h3 className="text-base font-semibold text-slate-900">Target Race</h3>
        </div>
        <div className="text-xs text-slate-500">{subtitle}</div>
      </div>

      <div className="px-5 pt-4 pb-2 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
            Showing
          </div>
          <div className="text-base font-bold text-slate-900">
            {dayjs(currentDate).format("MMM D, YYYY")}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePlayPause}
            className="w-9 h-9 rounded-full bg-violet-700 hover:bg-violet-800 text-white flex items-center justify-center shadow-sm"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <PauseIcon style={{ fontSize: 18 }} />
            ) : (
              <PlayArrowIcon style={{ fontSize: 20 }} />
            )}
          </button>
          <div className="flex items-center gap-0.5 bg-slate-50 rounded-full p-0.5 border border-slate-200">
            {SPEED_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={`px-2 py-0.5 text-xs rounded-full font-medium transition-colors ${
                  speed === s
                    ? "bg-white shadow text-violet-700"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-5 pb-1">
        <input
          type="range"
          min={0}
          max={Math.max(0, dateCount - 1)}
          value={safeIdx}
          onChange={(e) => {
            setIsPlaying(false);
            setIdx(parseInt(e.target.value, 10));
          }}
          className="w-full accent-violet-700 cursor-pointer"
        />
        <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1">
          <span>{dayjs(dates[0]).format("MMM D")}</span>
          <span>{dayjs(dates[dateCount - 1]).format("MMM D")}</span>
        </div>
      </div>

      <div className="px-5 pb-4 pt-2">
        <div
          className="relative"
          style={{ height: employees.length * ROW_HEIGHT }}
        >
          {employees.map((emp, origIdx) => {
            const position = positionById[emp.id] ?? origIdx;
            const competitionRank = competitionRankById[emp.id] ?? origIdx + 1;
            const value = emp[seriesKey]?.[safeIdx];
            const leftPct = valueToLeftPct(value);
            const color = TIMELINE_COLORS[origIdx % TIMELINE_COLORS.length];
            const isLeader = competitionRank === 1;
            // Diverging-bar geometry (variant="bar"): a bar grows from the
            // center target line (50%) out to the value's position. Geometry
            // (which side the bar extends) is purely where the value sits;
            // goodness (green vs red) depends on the metric's direction —
            // labor deltas want under target (left = good), sales-driver
            // ratio deltas want over target (right = good). Null value → no bar.
            const hasValue = value !== null && value !== undefined;
            const extendsLeft = hasValue && value <= centerValue;
            const onGoodSide =
              hasValue &&
              (direction === "higher_is_better"
                ? value >= centerValue
                : value <= centerValue);
            const barColor = onGoodSide ? "#10b981" : "#f43f5e";
            const barGeom =
              leftPct === null
                ? null
                : extendsLeft
                ? { left: leftPct, width: Math.max(0, 50 - leftPct), color: barColor }
                : { left: 50, width: Math.max(0, leftPct - 50), color: barColor };
            // Winners by competition rank so tied front-runners both get
            // the trophy even when winnersCount is small. A store with
            // null data shouldn't be a winner; the null branch above
            // assigns sequential ranks at the bottom, so this filters
            // them naturally.
            const isWinner =
              winnerMode === "hit_target"
                ? onGoodSide
                : hasValue && competitionRank <= winnersCount;
            return (
              <div
                key={emp.id}
                className="absolute left-0 right-0 flex items-center gap-2.5"
                style={{
                  transform: `translateY(${position * ROW_HEIGHT}px)`,
                  transition: "transform 500ms cubic-bezier(0.4, 0, 0.2, 1)",
                  height: ROW_HEIGHT - 6,
                  top: 0,
                }}
              >
                <div className="flex items-center justify-center w-5 text-xs font-semibold text-slate-500">
                  {competitionRank}
                </div>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-semibold shrink-0"
                  style={{ backgroundColor: color }}
                >
                  {initials(emp.full_name)}
                </div>
                <div className="w-28 shrink-0 min-w-0">
                  {/* Flex row so a long name truncates with an ellipsis
                      but the trophy stays visible — same pattern as the
                      bar race replay. */}
                  <div className="flex items-center gap-1 text-sm font-medium text-slate-900 min-w-0">
                    <span className="truncate min-w-0">{emp.full_name}</span>
                    {isWinner && (
                      <EmojiEventsIcon
                        className="text-amber-400 shrink-0"
                        style={{ fontSize: 12 }}
                      />
                    )}
                  </div>
                  {showStoreTag && emp.store_name && (
                    <div className="text-[10px] text-slate-400 -mt-0.5 truncate">
                      {emp.store_name}
                    </div>
                  )}
                </div>
                {/* Track + target line + marker. Track height stays
                    fixed and tall enough to contain the larger leader
                    marker without clipping. */}
                <div
                  className={`flex-1 min-w-0 relative ${
                    variant === "bar" ? "h-10" : "h-7"
                  }`}
                >
                  {variant === "bar" ? (
                    <>
                      {/* Faint good/bad tint halves so which side is good
                          reads even before the bars. The good half follows
                          the metric's direction: under target (left) for
                          lower_is_better, over target (right) for
                          higher_is_better. */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 rounded-l"
                        style={{
                          left: 0,
                          right: "50%",
                          height: 18,
                          backgroundColor:
                            direction === "higher_is_better"
                              ? "rgba(244,63,94,0.07)"
                              : "rgba(16,185,129,0.07)",
                        }}
                        aria-hidden
                      />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 rounded-r"
                        style={{
                          left: "50%",
                          right: 0,
                          height: 18,
                          backgroundColor:
                            direction === "higher_is_better"
                              ? "rgba(16,185,129,0.07)"
                              : "rgba(244,63,94,0.07)",
                        }}
                        aria-hidden
                      />
                      {/* Center target line — centered in the track (equal
                          space above and below), leaving room above it for the
                          per-row target number. */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-0.5 h-5 bg-slate-400"
                        style={{ left: "50%" }}
                        aria-hidden
                      />
                      {/* Diverging bar from the target line to the value. Only
                          the OUTER end is rounded; the end that meets the
                          target line stays square so it reads as anchored to
                          the target. */}
                      {barGeom && (
                        <div
                          className="absolute top-1/2"
                          style={{
                            left: `${barGeom.left}%`,
                            width: `${barGeom.width}%`,
                            transform: "translateY(-50%)",
                            height: 14,
                            backgroundColor: barGeom.color,
                            opacity: isLeader ? 1 : 0.85,
                            ...(extendsLeft
                              ? { borderTopLeftRadius: 3, borderBottomLeftRadius: 3 }
                              : { borderTopRightRadius: 3, borderBottomRightRadius: 3 }),
                            transition:
                              "left 500ms cubic-bezier(0.4, 0, 0.2, 1), width 500ms cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        />
                      )}
                      {/* This store's own target — the number the manager
                          entered (percent points), sitting above the center
                          line. Each store's target differs, so it's a per-row
                          label. */}
                      {emp.target_value !== null && emp.target_value !== undefined && (
                        <div
                          className="absolute text-[9px] font-semibold text-slate-500 leading-none"
                          style={{ left: "50%", top: 0, transform: "translateX(-50%)" }}
                        >
                          {`${+emp.target_value}%`}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Marker variant: dot slides on a neutral track. */}
                      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-slate-100 rounded-full" />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-0.5 h-5 bg-slate-300"
                        style={{ left: "50%" }}
                        aria-hidden
                      />
                      {leftPct !== null && (
                        <div
                          className="absolute top-1/2 rounded-full shadow"
                          style={{
                            left: `${leftPct}%`,
                            transform: "translate(-50%, -50%)",
                            width: isLeader ? 16 : 12,
                            height: isLeader ? 16 : 12,
                            backgroundColor: color,
                            // Leader pops at full opacity; everyone else is
                            // slightly muted, matching the bar race replay.
                            opacity: isLeader ? 1 : 0.85,
                            transition: "left 500ms cubic-bezier(0.4, 0, 0.2, 1)",
                            border: "2px solid white",
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
                <div className="text-sm font-semibold text-slate-900 w-16 text-right shrink-0">
                  {formatValue(value, valueFormat)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
